import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Prompt Review — kick off + read back AI-driven prompt quality reviews.
// Backed by /api/PromptReview/* on the Tenant API. The backend's
// PROMPT_REVIEW workflow runs asynchronously (~25 seconds for one prompt);
// the frontend polls for the result row by (promptKind, promptId) once
// it's been triggered.
// ─────────────────────────────────────────────────────────────────────────────

/** "Prebuilt" | "System" | "AgentInstructions" | "SkillBody". */
export type PromptKind = 'Prebuilt' | 'System' | 'AgentInstructions' | 'SkillBody';

/** "Pending" | "Accepted" | "Edited" | "Dismissed". */
export type ReviewerAction = 'Pending' | 'Accepted' | 'Edited' | 'Dismissed';

export interface ReviewSingleResponse {
  /** Opaque process key for tracking the workflow run (Guid hex string). */
  processKey: string;
  /** "Reviewing" — workflow is in flight. */
  status: string;
  /** Human-readable description of what the operator triggered. */
  title: string;
}

/**
 * Per-dimension scores. Null when the dimension was not applicable
 * (e.g. citation/clinical_accuracy/clinical_safety on non-clinical prompts).
 */
export interface PromptReviewScores {
  grammar: number | null;
  clarity: number | null;
  conciseness: number | null;
  tone: number | null;
  specificity: number | null;
  efficacy: number | null;
  behavior_not_mechanics: number | null;
  citation: number | null;
  consistency: number | null;
  clinical_accuracy: number | null;
  clinical_safety: number | null;
}

export interface PromptReviewResult {
  id: number;
  runId: number;
  promptKind: PromptKind;
  promptId: number;
  originalBody?: string | null;
  originalBodyHash?: string | null;
  /** JSON string — parsed via JSON.parse into PromptReviewScores. */
  scores_Json: string;
  aggregateScore: number;
  needsReview: boolean;
  suggestedRewrite?: string | null;
  rationale?: string | null;
  /** Comma-separated flag names (e.g. "clinical_accuracy_concern,phi_leak"). */
  autoFlags?: string | null;
  reviewerAction: ReviewerAction;
  reviewedAtUtc?: string | null;
  reviewedBy?: number | null;
  reviewerNote?: string | null;
  createdDate?: string | null;
}

export interface PromptReviewResultAction {
  id: number;
  resultId: number;
  action: ReviewerAction;
  actionAtUtc: string;
  actionBy: number;
  note?: string | null;
  previousAction?: ReviewerAction | null;
  createdDate?: string | null;
}

/**
 * Trigger a review of one prompt. Returns immediately with a processKey;
 * the workflow runs asynchronously for ~25 seconds. Poll
 * <see cref="getResultsForPrompt"/> to see when the result row lands.
 */
export async function reviewSingle(
  promptKind: PromptKind,
  promptId: number,
): Promise<ReviewSingleResponse> {
  const wrapped = await apiPost<OperationResult<ReviewSingleResponse>>(
    '/api/PromptReview/Single',
    { promptKind, promptId },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to start prompt review');
  }
  return wrapped.data;
}

/** Fetch a persisted review result by id. Returns null on 404. */
export async function getResult(id: number): Promise<PromptReviewResult | null> {
  try {
    const wrapped = await apiGet<OperationResult<PromptReviewResult>>(
      `/api/PromptReview/Result/${id}`,
    );
    if (!wrapped.isSuccess || !wrapped.data) return null;
    return wrapped.data;
  } catch {
    return null;
  }
}

/**
 * List recent review results for a specific prompt, latest first. Used by
 * the per-prompt history surface AND by the polling loop after triggering
 * a new review.
 */
export async function getResultsForPrompt(
  promptKind: PromptKind,
  promptId: number,
  take = 20,
): Promise<PromptReviewResult[]> {
  const wrapped = await apiGet<OperationResult<PromptReviewResult[]>>(
    `/api/PromptReview/Prompt/${promptKind}/${promptId}/Results?take=${take}`,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load reviews');
  return wrapped.data ?? [];
}

/**
 * Update the reviewer's action on a result row. The backend writes both
 * the result-row update AND an audit-history row in
 * ai.PromptReviewResultAction transactionally.
 */
export async function updateReviewerAction(
  resultId: number,
  action: ReviewerAction,
  note?: string,
): Promise<void> {
  const wrapped = await apiPost<OperationResult<unknown>>(
    `/api/PromptReview/Result/${resultId}/Action`,
    { action, note },
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to save action');
}

/** Reviewer-action audit trail for a result row, latest first. */
export async function getReviewerActionHistory(
  resultId: number,
): Promise<PromptReviewResultAction[]> {
  const wrapped = await apiGet<OperationResult<PromptReviewResultAction[]>>(
    `/api/PromptReview/Result/${resultId}/History`,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load history');
  return wrapped.data ?? [];
}
